import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Form, Spinner, Alert } from 'react-bootstrap';
import { FaExclamationTriangle } from 'react-icons/fa';
import { computeBranding } from '../utils/useAgentDetails';
import { handleTokenAndPostRequest, formatCurrency, trackEvent } from '../utils/utils';

/**
 * Modal that lets a homeowner push back on the AI valuation shown in the
 * Sale Estimate section of /property-profile.
 *
 * Captures: their minimum acceptable selling price (slider) + a free-text
 * justification. On save it POSTs to /save-valuation-feedback which:
 *   1. inserts a row in production.user_property_notes
 *   2. emails the agent (event_type='valuation_feedback')
 */
function ValuationFeedbackModal({
  show,
  onHide,
  agentDetails,
  propertyId,
  propertySlug,
  addressText,
  aiValuation,
  aiValuationLower,
  aiValuationUpper,
  onSaveSuccess,
  onRequireSignIn,
}) {
  const { accentColor, buttonTextColor } = computeBranding(agentDetails) || {};

  // Slider range: wide envelope around the AI midpoint so the user can express
  // a wildly different number if the AI is way off. Roughly 10%–300% of mid,
  // floored at $50k, snapped to $5k steps.
  const { sliderMin, sliderMax, sliderStep, defaultPrice } = useMemo(() => {
    const mid = Number(aiValuation) || 1000000;
    const lo  = Math.max(50000, Math.round((mid * 0.1) / 5000) * 5000);
    const hi  = Math.round((mid * 3.0) / 5000) * 5000;
    return {
      sliderMin: lo,
      sliderMax: hi > lo ? hi : lo + 1000000,
      sliderStep: 5000,
      defaultPrice: Math.round(mid / 5000) * 5000,
    };
  }, [aiValuation, aiValuationLower, aiValuationUpper]);

  const [minPrice, setMinPrice]   = useState(defaultPrice);
  const [note, setNote]           = useState('');
  const [isSaving, setIsSaving]   = useState(false);
  const [error, setError]         = useState('');
  const [submitted, setSubmitted] = useState(false);

  // Reset state when the modal is re-opened with a different valuation.
  useEffect(() => {
    if (show) {
      setMinPrice(defaultPrice);
      setNote('');
      setError('');
      setSubmitted(false);
    }
  }, [show, defaultPrice]);

  const handleClose = () => {
    if (isSaving) return;
    onHide();
  };

  const handleSubmit = async () => {
    setError('');
    if (!note.trim()) {
      setError('Please add a short note explaining what you think the AI is missing.');
      return;
    }

    const jwt_token   = localStorage.getItem('jwt_token');
    const param_token = localStorage.getItem('param_token');
    if (!jwt_token && !param_token) {
      trackEvent('modal_open_sign_in', { trigger: 'valuation_feedback' });
      if (onRequireSignIn) onRequireSignIn();
      return;
    }

    setIsSaving(true);
    try {
      trackEvent('submit_valuation_feedback', {
        property_id: propertyId,
        user_min_selling_price: minPrice,
        ai_valuation: aiValuation,
      });
      const response = await handleTokenAndPostRequest(
        `${window.api_host}/save-valuation-feedback`,
        {
          property_id:           propertyId,
          property_slug:         propertySlug,
          property_address:      addressText,
          landing_page_slug:     agentDetails?.landing_page_slug,
          user_min_selling_price: minPrice,
          note:                  note.trim(),
          ai_valuation:          aiValuation,
          ai_valuation_lower:    aiValuationLower,
          ai_valuation_upper:    aiValuationUpper,
        },
        { 'Content-Type': 'application/json' }
      );
      if (response?.data?.status === 'success') {
        setSubmitted(true);
        if (onSaveSuccess) onSaveSuccess();
      } else {
        setError(response?.data?.message || 'Could not save feedback. Please try again.');
      }
    } catch (err) {
      console.error('Valuation feedback error:', err);
      setError('Could not save feedback. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const delta = (Number(minPrice) || 0) - (Number(aiValuation) || 0);
  const deltaPct = aiValuation ? (delta / Number(aiValuation)) * 100 : 0;

  return (
    <Modal fullscreen="sm-down" style={{ zIndex: 9999 }} show={show} onHide={handleClose} centered>
      <Modal.Header closeButton style={{ border: 'none', paddingBottom: 0 }}>
        <Modal.Title style={{ fontSize: '1.15rem', fontWeight: 700, color: '#111', letterSpacing: '-0.01em', display: 'flex', alignItems: 'center', gap: 8 }}>
          <FaExclamationTriangle style={{ color: '#f59e0b' }} />
          Tell the agent what we missed
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: '12px 24px 20px' }}>
        {submitted ? (
          <div style={{ textAlign: 'center', padding: '12px 0 4px' }}>
            <div style={{ fontSize: '1.05rem', fontWeight: 600, color: '#111827', marginBottom: 6 }}>
              Thanks — your feedback has been sent to {agentDetails?.agent_first_name || 'your agent'}.
            </div>
            <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: 0 }}>
              They'll review the details and reach out if they need more information.
            </p>
          </div>
        ) : (
          <>
            <p style={{ fontSize: '0.88rem', color: '#4b5563', lineHeight: 1.5, marginBottom: 14 }}>
              Our AI bases its estimate on comparable sales and public data, so it can miss recent renovations, unique
              features, or off-market context. Tell us your minimum acceptable selling price and what we should know
              about the property — we'll pass it straight to the agent.
            </p>

            {Number(aiValuation) > 0 && (
              <div style={{ background: '#f0f7fa', borderLeft: '3px solid #0a9fa6', padding: '8px 12px', borderRadius: 4, marginBottom: 14, fontSize: '0.82rem', color: '#374151' }}>
                <strong>Current AI estimate:</strong> {formatCurrency(aiValuation)}
                {aiValuationLower && aiValuationUpper && (
                  <span style={{ color: '#6b7280' }}> ({formatCurrency(aiValuationLower)} – {formatCurrency(aiValuationUpper)})</span>
                )}
              </div>
            )}

            <Form.Group style={{ marginBottom: 16 }}>
              <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#111', marginBottom: 6 }}>
                Your minimum acceptable selling price
              </Form.Label>
              <div style={{ textAlign: 'center', fontSize: '1.6rem', fontWeight: 700, color: accentColor || '#0a9fa6', marginBottom: 4 }}>
                {formatCurrency(minPrice)}
              </div>
              {Number(aiValuation) > 0 && (
                <div style={{ textAlign: 'center', fontSize: '0.75rem', color: delta > 0 ? '#059669' : (delta < 0 ? '#dc2626' : '#6b7280'), marginBottom: 8 }}>
                  {delta === 0
                    ? 'matches the AI estimate'
                    : `${formatCurrency(Math.abs(delta))} (${Math.abs(deltaPct).toFixed(1)}%) ${delta > 0 ? 'above' : 'below'} AI estimate`}
                </div>
              )}
              <input
                type="range"
                className="vlf-price-slider"
                min={sliderMin}
                max={sliderMax}
                step={sliderStep}
                value={minPrice}
                onChange={(e) => setMinPrice(Number(e.target.value))}
                disabled={isSaving}
                style={{
                  ['--vlf-fill']: `${((minPrice - sliderMin) / (sliderMax - sliderMin)) * 100}%`,
                  ['--vlf-accent']: accentColor || '#0a9fa6',
                }}
              />
              <style>{`
                input[type=range].vlf-price-slider {
                  -webkit-appearance: none !important;
                  -moz-appearance: none !important;
                  appearance: none !important;
                  width: 100% !important;
                  height: 26px !important;
                  background: transparent !important;
                  padding: 0 !important;
                  margin: 8px 0 !important;
                  border: none !important;
                  outline: none !important;
                  cursor: pointer;
                }
                /* WebKit / Blink track */
                input[type=range].vlf-price-slider::-webkit-slider-runnable-track {
                  height: 6px;
                  border-radius: 999px;
                  background: linear-gradient(
                    to right,
                    var(--vlf-accent) 0%,
                    var(--vlf-accent) var(--vlf-fill),
                    #e5e7eb var(--vlf-fill),
                    #e5e7eb 100%
                  );
                  border: none;
                }
                /* WebKit / Blink thumb */
                input[type=range].vlf-price-slider::-webkit-slider-thumb {
                  -webkit-appearance: none;
                  appearance: none;
                  width: 22px;
                  height: 22px;
                  border-radius: 50%;
                  background: #ffffff;
                  border: 3px solid var(--vlf-accent);
                  box-shadow: 0 2px 6px rgba(0,0,0,0.25);
                  cursor: pointer;
                  margin-top: -8px; /* centre on 6px track */
                }
                /* Firefox track */
                input[type=range].vlf-price-slider::-moz-range-track {
                  height: 6px;
                  border-radius: 999px;
                  background: #e5e7eb;
                  border: none;
                }
                input[type=range].vlf-price-slider::-moz-range-progress {
                  height: 6px;
                  border-radius: 999px;
                  background: var(--vlf-accent);
                }
                /* Firefox thumb */
                input[type=range].vlf-price-slider::-moz-range-thumb {
                  width: 22px;
                  height: 22px;
                  border-radius: 50%;
                  background: #ffffff;
                  border: 3px solid var(--vlf-accent);
                  box-shadow: 0 2px 6px rgba(0,0,0,0.25);
                  cursor: pointer;
                }
                input[type=range].vlf-price-slider:focus { outline: none; }
                input[type=range].vlf-price-slider::-moz-focus-outer { border: 0; }
              `}</style>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#9ca3af', marginTop: 2 }}>
                <span>{formatCurrency(sliderMin)}</span>
                <span>{formatCurrency(sliderMax)}</span>
              </div>
            </Form.Group>

            <Form.Group style={{ marginBottom: 4 }}>
              <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#111', marginBottom: 6 }}>
                What is the AI missing? <span style={{ color: '#9ca3af', fontWeight: 400 }}>(required)</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Recently renovated kitchen and bathrooms, north-facing rear yard, DA approval for second storey, comparable sale at 12 Smith St last month for $X."
                disabled={isSaving}
                maxLength={2000}
                style={{ fontSize: '0.88rem', borderRadius: 8 }}
              />
            </Form.Group>

            {error && (
              <Alert variant="danger" style={{ fontSize: '0.82rem', padding: '8px 12px', marginTop: 10, marginBottom: 0 }}>
                {error}
              </Alert>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer style={{ borderTop: '1px solid #eceff3', padding: '10px 24px', background: '#fafafa' }}>
        <Button variant="link" onClick={handleClose} disabled={isSaving} style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.88rem' }}>
          {submitted ? 'Close' : 'Cancel'}
        </Button>
        {!submitted && (
          <Button
            onClick={handleSubmit}
            disabled={isSaving}
            style={{ backgroundColor: accentColor || '#0a9fa6', borderColor: accentColor || '#0a9fa6', color: buttonTextColor || '#fff', fontWeight: 600, fontSize: '0.88rem', padding: '6px 18px', borderRadius: 8 }}
          >
            {isSaving ? (<><Spinner size="sm" animation="border" style={{ marginRight: 6 }} /> Sending…</>) : 'Send to agent'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default ValuationFeedbackModal;
